'use client';
import { ExpectedLoyaltyPoints } from '@eliadmin/core/src/components/loyalty/expected-loyalty-points';

import { useLoyalty } from '@eliadmin/core/src/components/nt/account/shell/use-loyalty';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { fillTemplate, pluralText } from '@eliadmin/core/src/utils/nt/text';

/** „JN“ from „Jana Nováková“; the e-mail's first letter when the name is missing. */
function initials(name?: string, surname?: string, email?: string) {
  const letters = [name, surname].map(part => part?.trim().charAt(0) ?? '').join('');
  return (letters || email?.charAt(0) || '').toLocaleUpperCase('sk');
}

/**
 * Figma „User“ of „Account / Sidebar“ (205:11128): 48 px initials avatar (#F6FBF7, Body/L Bold
 * #0E6A20), the name (Body/L Bold) and the login e-mail (Caption #5D655D), 12 px apart.
 */
export function AccountUser({ className }: { className?: string }) {
  const customer = useCustomerStore(s => s.me?.customer);
  if (!customer) return null;
  const name = [customer.name, customer.surname].filter(Boolean).join(' ');

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      <span
        aria-hidden="true"
        className="nt-body-l-bold flex size-12 shrink-0 items-center justify-center rounded-full bg-[#F6FBF7] text-[#0E6A20]"
      >
        {initials(customer.name, customer.surname, customer.email)}
      </span>
      <div className="flex min-w-0 flex-col">
        {name ? <p className="nt-body-l-bold truncate text-[#0D110D]">{name}</p> : null}
        <p className="nt-caption truncate text-[#5D655D]">{customer.email}</p>
      </div>
    </div>
  );
}

/**
 * Figma „Loyalty“ (205:11128): #F6FBF7 box, radius 12, padding 12 / 14, the green star and
 * „1 240 vernostných bodov“ (Body/M Bold) over „= zľava 12,40 € na ďalší nákup“ (Caption #363D36).
 * Only with the store's loyalty program (`useLoyalty`).
 */
export function LoyaltyBox({ className }: { className?: string }) {
  const texts = useAccountTexts();
  const loyalty = useLoyalty();
  if (!loyalty || !texts.points) return <ExpectedLoyaltyPoints className={className} />;

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5 rounded-xl bg-[#F6FBF7] px-3.5 py-3',
        className,
      )}
    >
      {/* Figma draws the outline star; the shared icon set has only the filled cut (reported). */}
      <Icon name="star" size={20} className="text-[#127A26]" />
      <div className="flex min-w-0 flex-col">
        <p className="nt-body-m-bold text-[#0D110D]">
          {pluralText(texts.points, loyalty.points, { pocet: loyalty.formattedPoints })}
        </p>
        {texts.pointsValue ? (
          <p className="nt-caption text-[#363D36]">
            {fillTemplate(texts.pointsValue, { suma: loyalty.value })}
          </p>
        ) : null}
        <ExpectedLoyaltyPoints />
      </div>
    </div>
  );
}

/**
 * Figma „Sign out“: Body/L #363D36 with the 20 px sign-out icon (#5D655D). A plain link – the core
 * sign-out route clears the session cookie and redirects.
 */
export function SignOutLink({
  className,
  labelClassName,
}: {
  className?: string;
  /** The compact navigation hides the label visually on phones. */
  labelClassName?: string;
}) {
  const { t } = useTranslations();

  return (
    <a
      href={ROUTES.SIGN_OUT()}
      className={cn(
        'nt-body-l flex items-center gap-3 rounded-xl text-[#363D36] transition-colors hover:bg-[#F5F6F3] hover:text-[#0D110D] focus-visible:shadow-focus focus-visible:outline-none',
        className,
      )}
    >
      <Icon name="sign-out" size={20} className="text-[#5D655D]" />
      <span className={labelClassName}>{t('text258')}</span>
    </a>
  );
}
